import React, { FC, useEffect, useRef, useState } from 'react'
import styles from './PlatformCardBooking.module.css'
import s from '../../styles/common/Form.module.css'
import { Formik, FormikValues } from 'formik'
import CustomInput from '../UI/CustomInput/CustomInput'
import CustomButton from '../UI/CustomButton/CustomButton'
import * as yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { EstablishmentsType, PlaceType } from '../../redux/actions/types'
import { useAppDispatch, useAppSelector } from '../../redux/types/types'
import DropDown from '../DropDown/DropDown'
import { Calendar } from '../CustomCalendar/components'
import { format } from 'date-fns'
import DatePicker from 'react-datepicker'
import { createClaim } from '../../redux/actions/claimsAction'
import { waitTimer } from '../../utils/waitTimer'
import PATH from '../../navigation/path'
import { clearError } from '../../redux/reducers/claims/claimsSlice'

export type PlatformCardBookingPropsType = {
  bookingData?: {
    city: string
    startDate: string
    finishDate: string
    startTime: Date
    finishTime: Date
    guestsNumber: string
    titlePage: string
  } | null
  platformData: PlaceType | EstablishmentsType
  platformCity: string
}

const PlatformCardBooking: FC<PlatformCardBookingPropsType> = ({
  bookingData,
  platformData,
  platformCity,
}) => {
  const [openStartDate, setOpenStartDate] = useState(false)
  const [openFinishDate, setOpenFinishDate] = useState(false)

  const [openStartTime, setOpenStartTime] = useState(false)
  const [openFinishTime, setOpenFinishTime] = useState(false)

  const [startDateString, setStartDateString] = useState(
    bookingData?.startDate
      ? bookingData?.startDate?.slice(0, 10).split('-').reverse().join('.')
      : '',
  )
  const [finishDateString, setFinishDateString] = useState(
    bookingData?.finishDate
      ? bookingData?.finishDate?.slice(0, 10).split('-').reverse().join('.')
      : '',
  )

  const [timeStart, setTimeStart] = useState<Date | null>(
    bookingData?.startTime ? bookingData?.startTime : null,
  )
  const [timeFinish, setTimeFinish] = useState<Date | null>(
    bookingData?.finishTime ? bookingData?.finishTime : null,
  )

  const [selectedStartDate, setSelectedStartDate] = React.useState(new Date())
  const [selectedFinishDate, setSelectedFinishDate] = React.useState(new Date())

  const [validateError, setValidateError] = useState('')

  const getFormatDate = (time: Date | null) => {
    if (time !== null) {
      const hours: string = time.getHours().toString().padStart(2, '0')
      const minutes: string = time.getMinutes().toString().padStart(2, '0')
      const seconds: string = time.getSeconds().toString().padStart(2, '0')
      return `${hours}:${minutes}:${seconds}`
    } else {
      return null
    }
  }

  const onChangeStartDate = (date: Date) => {
    setSelectedStartDate(date)
    setStartDateString(format(date, 'dd.MM.yyyy'))
    setOpenStartDate(false)
  }

  const onChangeFinishDate = (date: Date) => {
    setSelectedFinishDate(date)
    setFinishDateString(format(date, 'dd.MM.yyyy'))
    setOpenFinishDate(false)
  }

  const [city, setCity] = useState('')

  const { token, user } = useAppSelector((state) => state.auth)
  const { error } = useAppSelector((state) => state.claims)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const validationSchema = yup.object().shape({
    guestsNumber: yup
      .number()
      .typeError('Введите количество в числовом значении')
      .integer('Количество должно быть целым числом')
      .positive('Количество должно быть положительным')
      .required('Поле не может быть пустым')
      .test('is-not-zero', 'Количество не должно быть равно нулю', (value) => value !== 0),
  })

  const customInput = <input type='text' placeholder='Когда?' className={styles.dateValue} />

  const handleSubmitHandler = async (values: FormikValues) => {
    if (startDateString === '') {
      setValidateError('Дата начала мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      return
    }

    if (finishDateString === '') {
      setValidateError('Дата окончания мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      return
    }

    if (timeStart === null) {
      setValidateError('Время начала мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      return
    }

    if (timeFinish === null) {
      setValidateError('Время окончания мероприятия обязательна')
      waitTimer(() => setValidateError(''))
      return
    }

    const startDate = `${startDateString.split('.').reverse().join('-')} ${getFormatDate(
      timeStart,
    )}`
    const endDate = `${finishDateString.split('.').reverse().join('-')} ${getFormatDate(
      timeFinish,
    )}`

    const resultAction = await dispatch(
      createClaim({
        token,
        userId: String(user.id),
        peopleCount: values.guestsNumber,
        placeId: String(platformData.id),
        startDate: startDate,
        endDate: endDate,
      }),
    )

    if (createClaim.rejected.match(resultAction)) {
      waitTimer(() => clearError())
    } else {
      navigate(PATH.reservationPlatformPage)
    }
  }

  const openStartDateDropDown = (e: any) => {
    e.stopPropagation()
    setOpenStartDate(!openStartDate)
  }

  const openStartTimeDropDown = (e: any) => {
    e.stopPropagation()
    setOpenStartTime(!openStartTime)
  }

  const openFinishDateDropDown = (e: any) => {
    e.stopPropagation()
    setOpenFinishDate(!openFinishDate)
  }

  const openFinishTimeDropDown = (e: any) => {
    e.stopPropagation()
    setOpenFinishTime(!openFinishTime)
  }

  const handleTimeStartChange = (time: any) => {
    setTimeStart(time)
  }

  const handleTimeFinishChange = (time: any) => {
    setTimeFinish(time)
  }

  const dropdownStartDataRef = useRef<HTMLDivElement>(null)
  const dropdownStartTimeRef = useRef<HTMLDivElement>(null)
  const dropdownFinishDataRef = useRef<HTMLDivElement>(null)
  const dropdownFinishTimeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownStartDataRef.current &&
        !dropdownStartDataRef.current.contains(event.target as Node)
      ) {
        setOpenStartDate(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownFinishDataRef.current &&
        !dropdownFinishDataRef.current.contains(event.target as Node)
      ) {
        setOpenFinishDate(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownStartTimeRef.current &&
        !dropdownStartTimeRef.current.contains(event.target as Node)
      ) {
        setOpenStartTime(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownFinishTimeRef.current &&
        !dropdownFinishTimeRef.current.contains(event.target as Node)
      ) {
        setOpenFinishTime(false)
      }
    }

    window.addEventListener('click', handleClickOutside)

    return () => {
      window.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    setCity(platformCity)
  }, [platformCity])

  return (
    <div className={styles.platformCardBooking}>
      <div className={styles.form}>
        <h1 className={styles.title}>Бронь зала</h1>
        <Formik
          initialValues={{
            city: city ? city : '',
            guestsNumber: bookingData ? bookingData.guestsNumber : '',
          }}
          validateOnBlur
          onSubmit={(values) => handleSubmitHandler(values)}
          validationSchema={validationSchema}
        >
          {(props) => {
            const { values, touched, errors, isValid, handleChange, handleBlur, handleSubmit } =
              props

            return (
              <div className={styles.formContainer}>
                <div className={styles.inputGroup}>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Компания</h4>
                    <div className={styles.companyBlock}>{platformData?.company.name}</div>
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Дата и время начала</h4>
                    <div
                      onClick={(e) => openStartDateDropDown(e)}
                      style={
                        startDateString ? { color: 'black' } : { color: 'rgba(120, 120, 120, 1)' }
                      }
                      className={styles.dateValue}
                    >
                      {startDateString ? startDateString : 'Дата начала'}
                      <DropDown
                        ref={dropdownStartDataRef}
                        className={styles.dropDown}
                        open={openStartDate}
                      >
                        <Calendar
                          selectedDate={selectedStartDate}
                          selectDate={(date) => onChangeStartDate(date)}
                        />
                      </DropDown>
                    </div>
                    <div className={styles.timeLeftBlock}>
                      <div className={styles.dateBlock} onClick={(e) => openStartTimeDropDown(e)}>
                        <div className={styles.dateBlock}>
                          <DatePicker
                            showTimeSelect
                            showTimeSelectOnly
                            selected={timeStart}
                            onChange={handleTimeStartChange}
                            timeIntervals={15}
                            timeCaption='Time'
                            customInput={customInput}
                            placeholderText='Время'
                            timeFormat='HH:mm'
                            dateFormat='HH:mm'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Дата и время окончания</h4>

                    <div
                      onClick={(e) => openFinishDateDropDown(e)}
                      style={
                        finishDateString ? { color: 'black' } : { color: 'rgba(120, 120, 120, 1)' }
                      }
                      className={styles.dateValue}
                    >
                      {finishDateString ? finishDateString : 'Дата окончания'}
                      <DropDown
                        ref={dropdownFinishDataRef}
                        className={styles.dropDown}
                        open={openFinishDate}
                      >
                        <Calendar
                          selectedDate={selectedFinishDate}
                          selectDate={(date) => onChangeFinishDate(date)}
                        />
                      </DropDown>
                    </div>
                    <div className={styles.timeRightBlock}>
                      <div className={styles.dateBlock} onClick={(e) => openFinishTimeDropDown(e)}>
                        <div className={styles.dateBlock}>
                          <DatePicker
                            showTimeSelect
                            showTimeSelectOnly
                            selected={timeFinish}
                            onChange={handleTimeFinishChange}
                            dateFormat='HH:mm'
                            timeIntervals={15}
                            timeCaption='Time'
                            timeFormat='HH:mm'
                            customInput={customInput}
                            placeholderText='Время'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.inputContainer}>
                    <h4 className={styles.subtitle}>Количество гостей</h4>
                    <div className={styles.inputContainerWithError}>
                      <CustomInput
                        type='text'
                        placeholder='Введите'
                        name='guestsNumber'
                        required
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.guestsNumber}
                        className={
                          errors.guestsNumber && touched.guestsNumber
                            ? `${s.inputError} ${styles.input}`
                            : styles.input
                        }
                      />
                      {touched.guestsNumber && errors.guestsNumber && (
                        <p style={{ marginBottom: 20 }} className={s.errorValidation}>
                          {errors.guestsNumber as string}
                        </p>
                      )}
                      <>
                        {error && (
                          <p
                            style={{ marginBottom: 20 }}
                            className={`${s.errorValidation} ${s.networkError}`}
                          >
                            {error as string}
                          </p>
                        )}
                      </>
                      <>
                        {validateError && (
                          <p
                            style={{ marginBottom: 20 }}
                            className={`${s.errorValidation} ${s.networkError}`}
                          >
                            {validateError}
                          </p>
                        )}
                      </>
                    </div>
                  </div>
                </div>
                <div className={styles.footerContainer}>
                  <CustomButton
                    title='Забронировать'
                    disabled={!isValid}
                    onClick={() => handleSubmit()}
                    type='submit'
                    className={styles.submitBtn}
                  />
                </div>
              </div>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default PlatformCardBooking
